import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Type,
  EventEmitter,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Note } from './note.model';
import { NoteService } from './note.service';
import { ConfirmationService, MessageService, SelectItem } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/types/table';
import moment from 'moment';
import { finalize } from 'rxjs/operators';
import { RoleAuthorizationService } from '../../../../services/role-authorization.service';
import {
  IRoleAuthorizationService,
  AuthorizationType,
} from '../../../../interfaces/role-authorization.interface';
import { Dialog } from 'primeng/dialog';
import { Editor } from 'primeng/editor';
import { Popover } from 'primeng/popover';
import { Select } from 'primeng/select';
import { toggleEllipsisPopover } from 'src/app/modules/crm/shared/utilities/ellipsis-popover.util';
import { ellipsisNoteHeaderOptions, ellipsisNoteOptions } from './constant';
import { filterTableData, printPDF, parseHTMLToString } from '../utilities/crm-table-utilities';
import { AddressCategoryType } from '../constants/enums';
import { AzureLoginService } from 'src/app/services/azure-login.service';
import { RolePermissionService } from '../../common/role-permission.service';
import { ServiceOrderService } from '../../service/service-order/service-order.service';
import { FeatureFlagsService } from '../../services/feature-flags.service';

/**
 * This is a generic component that can be used anywhere.
 * To use this, pass the identifierId and the category values
 */
@Component({
  standalone: false,
  selector: 'crm-notes',
  templateUrl: 'note.template.html',
  styleUrls: ['note.scss'],
  providers: [NoteService, MessageService],
})
export class NoteComponent implements OnInit, OnDestroy {
  // input
  @Input() scrollHeight = '540px'; // height of the notes section. 
  @Input() identifierId;
  @Input() disableAdd;
  /** When true notes can only be read: no adding, updating or deleting. */
  @Input() viewOnly = false;
  @Input() category: AddressCategoryType;
  @Input() disclaimer: string;
  @Input() otherData;
  @Input() parentComponent: Type<any>;
  @Input() parentCategory: string;
  @Input() showSmartAgentAssist = false;
  @Input() smartAgentAssistInProgress = false;

  sparkleGradientId = `saa-sparkle-${Math.random().toString(36).slice(2, 9)}`;

  onSmartAgentAssistClick() {
    if (this.smartAgentAssistInProgress) {
      return;
    }
    this.smartAgentAssistClick.emit();
  }

  @Output() dataRetrieved: EventEmitter<any> = new EventEmitter();
  @Output() smartAgentAssistClick: EventEmitter<void> = new EventEmitter();

  isSAAFeatureEnabled = false;
  isSAAUserEligible = false;

  // public
  public totalRecords = 1000;
  public rows: any[];
  public displayDialog: boolean;
  public isLoading: boolean;
  public isDeleting = false;
  /** Blocks another Save or Delete until the current request returns. */
  noteSaveInProgress = false;
  public note: Note = {};
  public selectedNote: Note;
  public newNote: boolean;
  public notes: any[] = [];
  public cols: any[];
  public typeList: SelectItem[];
  public currentType: SelectItem;
  public selectedColumns: any[];
  public selectedItem: any;
  public selectedRowIndex: number;
  public displayExport: boolean;
  public pdfTableOptions = {};
  public isViewMode = false;
  ellipsisHeaderOptions: any[] = ellipsisNoteHeaderOptions;
  ellipsisRowOptions: any[] = ellipsisNoteOptions.map((option) => ({ ...option }));
  private ellipsisRowOptionsBase: any[] = [];
  ellipsisRowOptionsSec: any;
  dateFormat = 'ddd MMM DD YYYY HH:mm:ss';

  // private
  private currentUser: any;
  private authDetails: IRoleAuthorizationService;
  querySubscription: any;
  querySubscriptionGetNotes: any;
  querySubscriptionSaveNotes: any;
  querySubscriptionEligibility: any;
  private buttons: any;
  buttonAuth: any = {};
  displayAlert: boolean;
  displayDiscardConfirm = false;
  @ViewChild('noteDialog') noteDialog: Dialog;
  @ViewChild('noteTypeSelect') noteTypeSelect: Select;
  @ViewChild('noteEditor') noteEditor: Editor;
  /** Pointer is over the note body, so typeahead must not open the Type list. */
  private pointerOverNoteEditor = false;
  private readonly onDocumentKeyDownCapture = (event: KeyboardEvent): void => {
    this.redirectKeysFromTypeSelect(event);
  };
  private openedNoteText = '';
  private openedNoteType: Note['noteType'];
  private closeNoteAfterDiscard = false;
  /** Scroll lock already applied by the open note window, restored after the discard prompt closes. */
  private noteDialogScrollLock: { variableName: string; variableValue: string } | null = null;
  offsetLimit: number;
   lastOffsetLimit: number;
  saveDisable = false;
  deleteDisabled = true;
  constructor(
    private noteService: NoteService,
    private roleService: RoleAuthorizationService,
    private azureService: AzureLoginService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private service: ServiceOrderService,
    private datePipe: DatePipe,
    private rps: RolePermissionService,
    private featureFlagService: FeatureFlagsService,
  ) {
    azureService.getFullName().subscribe((currentUser: any) => {
      this.currentUser = currentUser;
    });
  }

  getNotes() {
    this.offsetLimit = 0;
    this.lastOffsetLimit = 1000;
    this.querySubscription = this.noteService
      .getNotes(
        this.identifierId,
        this.category,
        this.offsetLimit,
        this.lastOffsetLimit,
        this.otherData,
      )
      .subscribe(
        ({ data }) => {
          this.isLoading = false;
          const modifedData = data.getNotesDetails.map((item) => {
            return { ...item };
          });
          const notesResult = this.validateNotes(modifedData);
          this.notes = !!this.notes ? [...this.notes, ...notesResult] : [...notesResult];
          this.rows = !!this.rows ? [...this.rows, ...notesResult] : [...this.notes];
          this.dataRetrieved.emit(this.notes);
        },
        (err) => {
          this.isLoading = false;
          throw err;
        },
      );
  }

  ngOnInit() {
    document.addEventListener('keydown', this.onDocumentKeyDownCapture, true);
    this.buttons = [{ name: 'Delete' }, { name: 'Save' }, { name: 'Add' }];
    this.authDetails = {
      authorizationType: AuthorizationType.Button,
      component: NoteComponent,
      generalArray: this.buttons,
    };
    if (this.parentComponent) {
      this.authDetails.parantComponent = this.parentComponent;
    }
    this.buttons = Object.assign(this.roleService.applyAuthorization(this.authDetails));
    for (const singleButton of this.buttons) {
      this.buttonAuth[singleButton.name] = true;
    }

    if (this.identifierId) {
      this.isLoading = true;
      this.offsetLimit = 0;

      this.getNotes();
    }

    // following assignment need to replaced with service call, once availabe
    this.cols = [
      { field: 'noteTypeDescription', header: 'Type', width: '11%', datatype: 'string' },
      { field: 'enteredBy', header: 'Entered By', width: '11%', datatype: 'string' },
      { field: 'noteText', header: 'Note', width: '50%', datatype: 'string' },
      { field: 'editDateTime', header: 'Last Updated Time', width: '12%', datatype: 'date' },
    ];

    this.typeList = [
      { label: 'External', value: 38 },
      { label: 'Internal', value: 39 },
      { label: 'System', value: 40 },
      { label: 'Supervisor', value: 41 },
      { label: 'Customer Knowledge', value: 34 },
      { label: 'Follow Up', value: 35 },
      { label: 'Escalation', value: 36 },
      { label: 'Info', value: 37 },
      { label: 'Status Change/Add Notes', value: 42 },
      { label: 'Inquiry', value: 133 }
    ];
    if (this.parentCategory === 'Claim' || this.parentCategory === 'Contract') {
      const permissionIternal = {
        category: this.parentCategory,
        operation: 'ADD',
        permission: 'Internal Comments',
        type: 'Select-Item',
      };
      if (!this.roleService.validateIfPermissionsExist(permissionIternal)) {
        const index = this.typeList.findIndex((r) => r.label === 'Internal');
        this.typeList.splice(index, 1);
      }
    }

    this.selectedColumns = this.cols;
    this.authDetails = {
      authorizationType: AuthorizationType.OptionList,
      component: NoteComponent,
      generalArray: this.typeList,
      operation: 'ADD',
    };
    if (this.parentComponent) {
      this.authDetails.parantComponent = this.parentComponent;
    }

    this.typeList = Object.assign(this.roleService.applyAuthorization(this.authDetails));

    this.doEvaluateRolePermissions();
    this.checkSAAUserEligibility();
  }

  private checkSAAUserEligibility() {
    if (!this.showSmartAgentAssist) {
      return;
    }
    this.isSAAFeatureEnabled = this.featureFlagService.isFeatureFlagEnabled('SAA');
    if (!this.isSAAFeatureEnabled) {
      return;
    }
    const azureUserId = this.azureService.accountId;
    if (!azureUserId) {
      this.isSAAUserEligible = false;
      return;
    }
    this.querySubscriptionEligibility = this.noteService
      .checkSAAUserEligibility(azureUserId)
      .subscribe(
        ({ data }) => {
          this.isSAAUserEligible = data?.getSAAUserEligibility === true;
          this.cdr.detectChanges();
        },
        () => {
          this.isSAAUserEligible = false;
        }
      );
  }

  doEvaluateRolePermissions() {
    this.ellipsisRowOptionsSec = {
      type: 'three-dots',
      operation: 'EDIT-DELETE',
      permission: 'Notes',
      category: this.parentCategory,
    };
    this.ellipsisRowOptions = this.rps.evaluate(
      this.ellipsisRowOptionsSec,
      ellipsisNoteOptions.map((option) => ({ ...option })),
    );
    this.ellipsisRowOptionsBase = this.ellipsisRowOptions.map((option) => ({ ...option }));
  }

  showDialogToAdd() {
    if (this.viewOnly) {
      return;
    }
    if (this.disclaimer) {
      if (this.disableAdd) {
        this.displayAlert = true;
      } else {
        this.confirmationService.confirm({
          message: this.disclaimer,
          accept: () => {
            this.addDailogSetup();
          },
        });
      }
    } else {
      if (!this.disableAdd) {
        this.addDailogSetup();
      }
    }
  }

  save(isDelete = false) {
    if (this.noteSaveInProgress) {
      return;
    }
    const errorMessage = this.validateNote();
    if (errorMessage !== '') {
      console.log('Inside Error Message: ' + errorMessage);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save note: ' + errorMessage,
      });
      return;
    }
    // set user info and validations
    this.note.lastUpdatedBy = this.currentUser;
    this.note.nodeAddedByUser = true;
    this.note.lastUpdatedTime = new Date();
    this.note.insertUserName = this.azureService.accountId;
    this.note.enteredBy = this.note.enteredBy ? this.note.enteredBy : this.azureService.accountId;
    this.note.enteredTime = this.note.enteredTime ? this.note.enteredTime : new Date();

    if (this.category === AddressCategoryType.Customer) {
      this.note.customerId = this.otherData;
    }

    if (isDelete) {
      this.isDeleting = true;
    }
    this.noteSaveInProgress = true;
    let noteSaveApplied = false;
    // Limit blank lines on the saved copy only, so the open editor does not jump.
    const noteToSave =
      !isDelete && this.note?.noteText
        ? { ...this.note, noteText: limitNoteBlankLines(this.note.noteText) }
        : this.note;
    this.querySubscriptionSaveNotes = this.noteService
      .saveNotes(noteToSave, this.identifierId, this.category)
      .pipe(
        finalize(() => {
          this.noteSaveInProgress = false;
          if (isDelete) {
            this.isDeleting = false;
          }
        }),
      )
      .subscribe(({ data, loading }: any) => {
        this.isLoading = loading;
        if (noteSaveApplied || !data) {
          return;
        }
        noteSaveApplied = true;
        this.service.isNoteAddedByUser.next(true);
        if (isDelete) {
          const index = this.notes.indexOf(this.selectedItem);
          this.notes = this.notes.filter((val, i) => i !== index);
          this.rows = [...this.notes];
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Note deleted successfully',
          });
        } else {
          const noteTypeDescription = this.typeList.find((t) => t.value === this.note.noteType);
          data.insertNotes.isEditable = true;
          data.insertNotes.canDelete = true;
          data.insertNotes.noteTypeDescription = noteTypeDescription && noteTypeDescription.label;

          const notes = [...this.notes];
          if (this.newNote) {
            notes.unshift(data.insertNotes);
          } else {
            notes[this.notes.indexOf(this.selectedItem)] = data.insertNotes;
          }

          this.rows = [...notes];
          this.notes = notes;
          this.dataRetrieved.emit(this.notes);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Note saved successfully',
          });
        }
        this.note.nodeAddedByUser = false;
        this.note = null;
        this.displayDialog = false;
        this.service.isNoteAddedByUser.next(false);
      });
  }

  delete() {
    this.note.isDeleted = true;
    this.save(true);
  }

  onRowDelete(event, popover?: Popover): void {
    popover?.hide();
    if (this.noteSaveInProgress || !this.canShowNoteDelete(this.selectedItem)) {
      return;
    }
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this Note ?',
      accept: () => {
        this.newNote = false;
        this.note = this.cloneNote(this.selectedItem);
        this.delete();
      },
    });
  }

  onRowSelect(event) {
    const noteToClone = this.selectedItem;
    this.newNote = false;
    // set selected row
    this.note = this.cloneNote(noteToClone);
    if (this.viewOnly && this.note) {
      this.note.isEditable = false;
      this.note.canDelete = false;
    }
    this.isViewMode = this.note && this.note.isEditable ? !this.note.isEditable : false;
    if (this.viewOnly) {
      this.isViewMode = true;
    }
    // set dropdown model
    this.currentType = this.typeList.find((t) => t.value === this.note.noteType);
    this.captureNoteSnapshot();
    this.displayDialog = true;
  }

  /**
   * The dialog close icon sets visible to false before we can stop it.
   * Put the window back and ask when the open note would be lost.
   */
  onNoteDialogVisibleChange(visible: boolean): void {
    if (visible || !this.noteHasUnsavedChanges()) {
      this.displayDialog = visible;
      if (!visible) {
        this.displayDiscardConfirm = false;
        this.releaseNoteDialogScrollLock();
      }
      return;
    }
    if (this.noteDialog) {
      this.noteDialog.visible = true;
    }
    this.displayDialog = true;
    this.captureNoteDialogScrollLock();
    this.displayDiscardConfirm = true;
  }

  cancelDiscardNote(): void {
    this.displayDiscardConfirm = false;
  }

  /**
   * The discard prompt is a second modal. Opening it recalculates the scrollbar
   * width as 0 and closing it releases the page lock, which nudges the note window.
   * Hold the original lock until the note window itself closes.
   */
  restoreNoteDialogScrollLock(): void {
    const lock = this.noteDialogScrollLock;
    if (!lock || !this.displayDialog) {
      return;
    }
    document.body.classList.add('p-overflow-hidden');
    document.body.classList.add('note-dialog-scroll-locked');
    if (lock.variableName && lock.variableValue) {
      document.body.style.setProperty(lock.variableName, lock.variableValue);
      document.body.style.setProperty('--note-dialog-scrollbar-width', lock.variableValue);
    }
  }

  /**
   * Close the prompt first, then the note window, so PrimeNG never has two modals
   * leaving at once (it only releases the page scroll lock when one is left).
   */
  confirmDiscardNote(): void {
    this.closeNoteAfterDiscard = true;
    this.displayDiscardConfirm = false;
    // Fallback in case the prompt's hide event does not fire.
    setTimeout(() => this.finishDiscardNote(), 500);
  }

  onDiscardDialogHide(): void {
    this.restoreNoteDialogScrollLock();
    this.finishDiscardNote();
  }

  private finishDiscardNote(): void {
    if (!this.closeNoteAfterDiscard) {
      return;
    }
    this.closeNoteAfterDiscard = false;
    this.displayDialog = false;
  }

  cloneNote(currentNote: Note): Note {
    const note = {};
    for (const prop in currentNote) {
      if (prop in currentNote) {
        note[prop] = currentNote[prop];
      }
    }
    return note;
  }

  loadDataOnScroll(event: TableLazyLoadEvent) {
    /* Callback to invoke when paging, sorting or filtering happens in lazy mode. Following are the available properties
    event.first = First row offset,
    event.rows = Number of rows per page
    event.sortField = Field name to sort with
    event.sortOrder = Sort order as number, 1 for asc and -1 for dec
    event.multiSortMeta: An array of SortMeta objects used in multiple columns sorting. Each SortMeta has field and order properties.
    event.filters: FilterMetadata object having field as key and filter value, filter matchMode as value
    event.globalFilter: Value of the global filter if available */

    // if row offeset is zero then the scroll has happend upwards
    // if nothing is done with the upward scroll it'll create an empty space, hance the reinitialization
    if (this.identifierId && this.notes && this.notes.length > 0) {
      this.rows = [...this.notes];
      const filterValue = this.noteGlobalFilterValue(event);
      if (filterValue) {
        this.rows = this.rows.filter((row) => this.filterGlobal(row, filterValue));
      }
      this.customSort(event);
    }
  }

  /**
   * The box still sends the input event. PrimeNG keeps that event as the lazy filter value,
   * so the typed letters are read here before the contains check runs.
   */
  onNotesGlobalFilter(event: Event, table: { filterGlobal: (value: string, matchMode: string) => void }): void {
    table.filterGlobal(this.typedInputValue(event), 'contains');
  }

  onTypeChange(event) {
    this.note.noteType = event.value.value;
  }

  /**
   * The notes accordion treats ArrowUp, ArrowDown, Home, and End as header navigation
   * and cancels the browser default. Stop those keys from leaving the rich text editor
   * so the caret can still move through lines and to the end of a line.
   */
  keepNoteEditorCaretKeys(event: KeyboardEvent): void {
    switch (event.code) {
      case 'ArrowUp':
      case 'ArrowDown':
      case 'Home':
      case 'End':
        event.stopPropagation();
        break;
      default:
        break;
    }
  }

  /**
   * The Type list stays focused after the dialog opens and treats any character as typeahead.
   * While the pointer is in the note body, move that focus into the editor.
   * Place the caret at the end so Update/edit does not jump to the start of the note.
   */
  onNoteDialogPointerMove(event: MouseEvent): void {
    if (!this.canEditOpenNote()) {
      this.pointerOverNoteEditor = false;
      return;
    }
    this.pointerOverNoteEditor = this.isPointerOverNoteEditor(event.clientX, event.clientY);
    if (!this.pointerOverNoteEditor || !this.isTypeSelectFocused()) {
      return;
    }
    const quill = this.noteEditor?.getQuill();
    if (!quill) {
      return;
    }
    if (this.noteTypeSelect?.overlayVisible) {
      this.noteTypeSelect.hide(false);
    }
    this.focusNoteEditorAtEnd(quill);
  }

  onNoteDialogPointerLeave(): void {
    this.pointerOverNoteEditor = false;
  }

  getFormattedDate(date) {
    return moment(new Date(date)).format(this.dateFormat);
  }

  /** Notes list only. Search, export, and the stored row keep the original HTML. */
  displayedNoteHtml(field: string, value: string): string {
    if (field === 'noteText' && value) {
      return limitNoteBlankLines(value);
    }
    return value;
  }

  ellipsisClick(event, item, popover: Popover, rowIndex) {
    this.selectedItem = item;
    this.selectedRowIndex = rowIndex;
    const rowOptions = this.ellipsisRowOptionsBase.filter((option) => option.value !== 'delete');
    if (this.canShowNoteDelete(item)) {
      rowOptions.push({ label: 'Delete', value: 'delete', callback: 'onRowDelete' });
    }
    this.ellipsisRowOptions = rowOptions;
    toggleEllipsisPopover(event, popover);
  }

  onClickExport() {
    this.pdfTableOptions = this.setTableWidths();
    this.displayExport = true;
  }

  onClickPrint(event, Popover: Popover) {
    let filteredData = filterTableData(this.rows, this.selectedColumns);
    filteredData = parseHTMLToString(filteredData);
    filteredData.forEach((item) => {
      item.editDateTime = this.convertDateTime(item.editDateTime);
    });
    const additionalTableOptions = this.setTableWidths();
    printPDF(this.selectedColumns, filteredData, additionalTableOptions);
    Popover.hide();
  }

  convertDateTime(date) {
    return this.datePipe.transform(date, 'MM/dd/yyyy hh:mm:ss a');
  }

  ellipsisOptionClick(event) {
    // ellipsis functionality goes here
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.onDocumentKeyDownCapture, true);
    if (this.querySubscription) {
      this.querySubscription.unsubscribe();
    }
    if (this.querySubscriptionGetNotes) {
      this.querySubscriptionGetNotes.unsubscribe();
    }
    if (this.querySubscriptionSaveNotes) {
      this.querySubscriptionSaveNotes.unsubscribe();
    }
    if (this.querySubscriptionEligibility) {
      this.querySubscriptionEligibility.unsubscribe();
    }
  }

  private customSort(event: TableLazyLoadEvent) {
    const sortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    if (!sortField) {
      return;
    }
    this.rows.sort((data1, data2) => {
      const value1 = data1[sortField];
      const value2 = data2[sortField];
      let result = null;

      if (value1 == null && value2 != null) {
        result = -1;
      } else if (value1 != null && value2 == null) {
        result = 1;
      } else if (value1 == null && value2 == null) {
        result = 0;
      } else if (typeof value1 === 'string' && typeof value2 === 'string') {
        result = value1.localeCompare(value2);
      } else {
        result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
      }

      return event.sortOrder * result;
    });
  }
  private validatePermissionsNotes(notes: Note[]) {
    const editInternal = this.roleService.validateIfPermissionsExist({
      category: this.parentCategory,
      operation: 'EDIT',
      permission: 'Internal Comments',
      type: 'Select-Item',
    });
    const deleteInternal = this.roleService.validateIfPermissionsExist({
      category: this.parentCategory,
      operation: 'DELETE',
      permission: 'Internal Comments',
      type: 'Select-Item',
    });
    const readInternalComments = this.roleService.validateIfPermissionsExist({
      category: this.parentCategory,
      operation: 'READ_ONLY',
      permission: 'Internal Comments',
      type: 'Select-Item',
    });
    const readSystem = this.roleService.validateIfPermissionsExist({
      category: this.parentCategory,
      operation: 'READ_ONLY',
      permission: 'System Notes',
      type: 'Select-Item',
    });
    notes.forEach((element) => {
      if (element.enteredBy === this.currentUser || this.roleService.userRole === 'Admin') {
        if (element.type === 'Internal' || element.noteTypeDescription === 'Internal') {
          element.isEditable = editInternal;
          element.canDelete = deleteInternal;
        } else {
          element.isEditable = true;
          element.canDelete = true;
        }
      }
    });
    notes = notes.filter((note) => {
      if (note.type === 'Internal' || note.noteTypeDescription === 'Internal') {
        if (readInternalComments || (this.parentCategory !== 'Claim' && this.parentCategory !== 'Contract')) {
          return note;
        }
      } else if (note.type === 'System' || note.noteTypeDescription === 'System') {
        if (readSystem) {
          return note;
        }
      } else {
        return note;
      }
    });
    return notes;
  }
  private validateNotes(notes: Note[]) {
    if (this.parentCategory === 'Claim' || this.parentCategory === 'Contract') {
      return this.validatePermissionsNotes(notes);
    }
    notes.forEach((element) => {
      if (element.enteredBy === this.currentUser || this.roleService.userRole === 'Admin') {
        element.isEditable = true;
        element.canDelete = true;
      }
    });
    return notes;
  }
  /** Typed text from the lazy event, or from the global filter metadata when the event value is the input event. */
  private noteGlobalFilterValue(event: TableLazyLoadEvent): string {
    return this.globalFilterText(event.globalFilter) || this.globalFilterText(this.lazyGlobalFilterValue(event));
  }

  private lazyGlobalFilterValue(event: TableLazyLoadEvent): unknown {
    const metadata = event.filters?.['global'];
    if (Array.isArray(metadata)) {
      return metadata[0]?.value;
    }
    return metadata?.value;
  }

  /**
   * The search box passes the input event through, as it did before the Angular upgrade.
   * A stringified event is not the typed text and must not be used as the search.
   */
  private globalFilterText(globalFilter: unknown): string {
    if (typeof globalFilter === 'string') {
      return this.isEventPlaceholder(globalFilter) ? '' : globalFilter;
    }
    if (Array.isArray(globalFilter)) {
      return globalFilter.map((entry) => this.globalFilterText(entry)).find((entry) => entry) ?? '';
    }
    if (globalFilter && typeof globalFilter === 'object') {
      const record = globalFilter as { value?: unknown; target?: { value?: unknown } };
      if (record.target && typeof record.target === 'object' && 'value' in record.target) {
        return this.globalFilterText(record.target.value);
      }
      if ('value' in record) {
        return this.globalFilterText(record.value);
      }
    }
    return '';
  }

  private typedInputValue(event: Event): string {
    const value = (event.target as HTMLInputElement | null)?.value;
    return typeof value === 'string' ? value : '';
  }

  private isEventPlaceholder(value: string): boolean {
    return value === '[object Object]' || value === '[object Event]' || value === '[object InputEvent]';
  }

  private filterGlobal(row, value) {
    for (const column of this.cols) {
      if (row[column.field] == null) {
        continue;
      }
      const rowValue: string = row[column.field].toString().toLowerCase();
      if (rowValue.includes(value.toLowerCase())) {
        return true;
      }
    }
    return false;
  }
  private canEditOpenNote(): boolean {
    return !!(this.displayDialog && this.note?.isEditable && !this.isViewMode);
  }

  private isPointerOverNoteEditor(x: number, y: number): boolean {
    const root = this.noteEditor?.getQuill()?.root as HTMLElement | undefined;
    if (!root) {
      return false;
    }
    const top = document.elementFromPoint(x, y);
    return !!top && (top === root || root.contains(top));
  }

  private isTypeSelectFocused(): boolean {
    const selectHost = this.noteTypeSelect?.el?.nativeElement as HTMLElement | undefined;
    const active = document.activeElement;
    return !!(selectHost && active && selectHost.contains(active));
  }

  /**
   * Runs before the Type list sees the key. A character typed with the pointer in the
   * note body is written there instead of opening the list.
   */
  private redirectKeysFromTypeSelect(event: KeyboardEvent): void {
    if (!this.pointerOverNoteEditor || !this.canEditOpenNote()) {
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey || event.key?.length !== 1) {
      return;
    }
    const selectHost = this.noteTypeSelect?.el?.nativeElement as HTMLElement | undefined;
    const target = event.target as Node | null;
    if (!selectHost || !target || !selectHost.contains(target)) {
      return;
    }
    const quill = this.noteEditor?.getQuill();
    if (!quill) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (this.noteTypeSelect.overlayVisible) {
      this.noteTypeSelect.hide(false);
    }
    const index = quill.getSelection()?.index ?? this.noteEditorCaretEndIndex(quill);
    this.focusNoteEditorAtIndex(quill, index);
    quill.insertText(index, event.key, 'user');
    quill.setSelection(index + 1, 0, 'silent');
  }

  /** Quill keeps a trailing newline; the last typing position is length - 1. */
  private noteEditorCaretEndIndex(quill: { getLength: () => number }): number {
    return Math.max(0, quill.getLength() - 1);
  }

  private focusNoteEditorAtEnd(quill: {
    focus: () => void;
    getLength: () => number;
    setSelection: (index: number, length: number, source?: string) => void;
  }): void {
    this.focusNoteEditorAtIndex(quill, this.noteEditorCaretEndIndex(quill));
  }

  private focusNoteEditorAtIndex(
    quill: {
      focus: () => void;
      setSelection: (index: number, length: number, source?: string) => void;
    },
    index: number,
  ): void {
    quill.focus();
    quill.setSelection(index, 0, 'silent');
  }

  private addDailogSetup() {
    this.newNote = true;
    this.note = {
      enteredBy: this.currentUser,
      enteredTime: new Date(),
      isEditable: true,
      canDelete: true,
    };
    this.currentType = this.typeList[0];
    this.note.noteType = this.currentType.value;
    this.captureNoteSnapshot();
    this.displayDialog = true;
  }

  private captureNoteDialogScrollLock(): void {
    const variableName = this.bodyScrollbarVariableName();
    const variableValue = variableName ? document.body.style.getPropertyValue(variableName) : '';
    this.noteDialogScrollLock = { variableName, variableValue };
    if (variableValue) {
      document.body.style.setProperty('--note-dialog-scrollbar-width', variableValue);
    }
    document.body.classList.add('note-dialog-scroll-locked');
  }

  releaseNoteDialogScrollLock(): void {
    if (this.displayDialog) {
      return;
    }
    document.body.classList.remove('note-dialog-scroll-locked');
    document.body.style.removeProperty('--note-dialog-scrollbar-width');
    this.noteDialogScrollLock = null;
  }

  private bodyScrollbarVariableName(): string {
    const style = document.body.style;
    for (let index = 0; index < style.length; index++) {
      const name = style.item(index);
      if (name && name.toLowerCase().includes('scrollbar')) {
        return name;
      }
    }
    return '';
  }

  /**
   * Same gates as the old dialog Delete button: Notes DELETE role, Delete button
   * authorization, and the note's own canDelete flag. View-only still hides it.
   */
  private canShowNoteDelete(note: Note): boolean {
    return !this.viewOnly && !!note?.canDelete && !!this.buttonAuth.Delete && this.hasNotesDeletePermission();
  }

  private hasNotesDeletePermission(): boolean {
    if (this.parentCategory === undefined) {
      return true;
    }
    const rolePermissions = Array.isArray(this.azureService.rolePermissions)
      ? this.azureService.rolePermissions
      : [];
    return rolePermissions.some(
      (permission) =>
        permission?.permissionDescription === 'Notes' &&
        permission?.permissionCategory === this.parentCategory &&
        (permission?.operationName === 'DELETE' || permission?.operationName === 'ALL'),
    );
  }

  private captureNoteSnapshot(): void {
    this.openedNoteText = this.note?.noteText || '';
    this.openedNoteType = this.note?.noteType;
  }

  private noteHasUnsavedChanges(): boolean {
    if (!this.note?.isEditable) {
      return false;
    }
    const textChanged = this.normalizedNoteText(this.note.noteText) !== this.normalizedNoteText(this.openedNoteText);
    const typeChanged = this.note.noteType !== this.openedNoteType;
    return textChanged || typeChanged;
  }

  /** Empty editor markup counts as blank. Any other markup is kept so formatting edits are caught. */
  private normalizedNoteText(html: string): string {
    if (!html || !this.plainNoteText(html)) {
      return '';
    }
    return html.trim();
  }

  private plainNoteText(html: string): string {
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  saveSystemNote = (
    type: AddressCategoryType,
    noteText: string,
    noteType,
    enteredBy: string = null,
    enteredTime: string = null,
    editDateTime: string = null,
  ) => {
    const typeList = [{ label: 'System', value: 40 }];
    const category = type;
    const note = {
      tenantId: null,
      noteType: noteType === 133 ? noteType : typeList[0].value,
      noteText,
      enteredBy: enteredBy ? enteredBy : 'System',
      enteredTime: enteredTime ? moment(enteredTime).format() : null,
      noteIdOriginal: null,
      external: null,
      isDeleted: null,
      editDateTime: editDateTime ? moment(editDateTime).format() : null,
      customerId : null
    };

    if (this.category === AddressCategoryType.Customer) {
      this.note.customerId = this.otherData;
      note.customerId = this.otherData;
    }

    return this.noteService.saveNotes(note, this.identifierId, category).subscribe(
      (result: any) => {
        const noteTypeDescription = this.typeList.find(
          (t) => t.value === result.data.insertNotes.noteType,
        );
        result.data.insertNotes.noteTypeDescription =
          noteTypeDescription && noteTypeDescription.label;
        const notes = [...this.notes];
        notes.unshift(result.data.insertNotes);
        this.rows = [...notes];
        this.notes = notes;
        this.dataRetrieved.emit(this.notes);
        this.cdr.detectChanges();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: noteType + ' Note saved successfully',
        });
      },
      (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add ' + noteType + ' note',
        });
      },
    );
  }; // tslint:disable-line

  // Need this because user can select different columns to view.
  private setTableWidths() {
    let options = {};
    const columnStyles = {};
    for (const index in this.selectedColumns) {
      if (index) {
        const headerName = this.selectedColumns[index].header;
        if (headerName === 'Type') {
          columnStyles[index] = { cellWidth: 4 };
        }
        if (headerName === 'Entered By') {
          columnStyles[index] = { cellWidth: 6 };
        }
        if (headerName === 'Last Updated Time') {
          columnStyles[index] = { cellWidth: 11 };
        }
      }
    }
    options = {
      columnStyles,
    };
    return options;
  }

  private validateNote() {
    let errorMessage = '';
    if (this.note && !this.note.isDeleted && this.note.noteText && this.note.noteText !== '') {
      if (this.note.noteText.includes('<img src=')) {
        errorMessage = 'Images are not allowed in the notes. Please add them as attachments.';
      }
    }
    return errorMessage;
  }
}

const MEDIA = /<(img|iframe|video|object|embed|table|ul|ol|pre)\b/i;
const NBSP = /&nbsp;|&#160;|&#xa0;/i;
const SINGLE_BLANK_LINE = '<p><br></p>';

/**
 * Quill 2 writes a blank line as an empty paragraph. With paragraph margins
 * removed, that paragraph has no height, so the gap disappears after save.
 * Keep one visible blank line and collapse extra blank lines around it.
 * Notes with no empty paragraph are returned unchanged.
 */
function limitNoteBlankLines(html: string): string {
  if (!html || !/<p\b/i.test(html)) {
    return html;
  }

  const matches = [...html.matchAll(/<p\b[^>]*>[\s\S]*?<\/p>/gi)];
  if (!matches.length || !matches.some((match) => isBlankParagraphHtml(match[0]))) {
    return html;
  }

  let result = '';
  let cursor = 0;
  let index = 0;
  while (index < matches.length) {
    const match = matches[index];
    const start = match.index ?? 0;
    result += html.slice(cursor, start);
    if (!isBlankParagraphHtml(match[0])) {
      result += match[0];
      cursor = start + match[0].length;
      index += 1;
      continue;
    }

    let end = start + match[0].length;
    let kept = isSingleVisibleBlank(match[0]) ? match[0] : '';
    let runIndex = index + 1;
    while (runIndex < matches.length) {
      const next = matches[runIndex];
      const between = html.slice(end, next.index ?? 0);
      if (between.trim() !== '' || !isBlankParagraphHtml(next[0])) {
        break;
      }
      if (!kept && isSingleVisibleBlank(next[0])) {
        kept = next[0];
      }
      end = (next.index ?? 0) + next[0].length;
      runIndex += 1;
    }
    const runLength = runIndex - index;
    result += runLength === 1 && kept ? kept : SINGLE_BLANK_LINE;
    cursor = end;
    index = runIndex;
  }
  result += html.slice(cursor);
  return result === html ? html : result;
}

function isBlankParagraphHtml(html: string): boolean {
  if (!/^<p\b/i.test(html) || MEDIA.test(html)) {
    return false;
  }
  const inner = html.replace(/^<p\b[^>]*>/i, '').replace(/<\/p>$/i, '');
  const text = inner
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;|&#160;|&#xa0;/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/[\s\u00a0]/g, '');
  return text.length === 0;
}

function isSingleVisibleBlank(html: string): boolean {
  const breakCount = html.match(/<br\b/gi)?.length ?? 0;
  const hasNbsp = NBSP.test(html);
  return (breakCount === 1 && !hasNbsp) || (breakCount === 0 && hasNbsp);
}
